/**
 * shared/lib/guardian-verification/normalize.ts
 *
 * Canonical normalization applied before SHAKE256 hashing of guardian
 * verification answers. Used by:
 *   - Initiator's QuestionCreator.tsx (invite creation: hashing each Q's answer)
 *   - Candidate's use-recipient-accept.ts (verify submit), reached from both
 *     client/src/pages/onboarding/GuardianInvitePage.tsx and
 *     client/src/pages/guardian-setup/components/RecipientAnswerPanel.tsx
 *
 * Both sides MUST apply the SAME function before hashing:
 *   hashSeedAnswer(normalizeVerificationAnswer(rawAnswer), saltHex)
 *
 * NB: hashSeedAnswer signature is (answer, saltHex) — answer first, salt second.
 *
 * ── What this absorbs, and what it deliberately does not ─────────────────────
 *
 * Absorbed: capitalisation, leading/trailing whitespace, internal whitespace
 * runs, unicode space and invisible-format characters (NBSP, ZWSP, soft hyphen —
 * what a paste or a mobile keyboard injects), punctuation and symbols,
 * apostrophes (including the curly U+2019 a phone substitutes), and LATIN
 * diacritics. "St. Kilda", "st kilda" and "St  Kilda" agree; so do "café" and
 * "cafe".
 *
 * NOT absorbed: different WORDING. "st kilda" and "st kilda beach" remain
 * distinct answers, and no copy may suggest otherwise.
 *
 * ── One form, not two (a rejected design, recorded so it does not return) ────
 *
 * An earlier build of this change kept the previous, narrower canonicalization
 * alongside this one and had the accept path submit the answer digested under
 * BOTH, with the server passing on either — so that invites created before the
 * widening kept matching without a migration. That was REJECTED under review:
 * the server sees only digests and cannot verify that two submitted digests are
 * encodings of one answer, so "either may match" is, to an attacker, simply
 * extra guesses per question. The rejected route schema allowed a primary digest
 * plus up to two alternates — 3 per question, so 3³ = 27 answer-triples per
 * attempt instead of 1, and 81 across the 3-attempt burn window rather than 3
 * (an honest client only ever produced 2 per question, i.e. 8 and 24 — the
 * attacker-relevant number is what the schema PERMITS, not what the client
 * sends). A code comment asserting "alternate encodings only" is not a control.
 * There is exactly ONE canonical form, and exactly one digest is submitted per
 * answer, so the guess budget is byte-identical to before this change.
 *
 * ── One form PER STORED ROW, still one digest per guess ─────────────────────
 *
 * Ledger #53 also accepted a cost: an invite created before the widening stored
 * a digest of the narrower form and "can no longer be matched, so it must be
 * re-sent." That cost is now removed — but NOT by reinstating the rejected
 * design above, and the difference is the whole point:
 *
 *   rejected  — the SUBMITTER sends several digests and the server passes on
 *               any. N digests = N guesses. Rejected, and still rejected.
 *   shipped   — the STORED ROW records which version the ward hashed under
 *               (`answer_canon_version`, migration 0129). The guardian's device
 *               is told that version and computes ONE digest under it. The
 *               submitter chooses nothing; the guess budget is unchanged.
 *
 * So this file now holds TWO canonical forms, and exactly one of them applies
 * to any given stored digest. See ./canon-version.ts for the version log and
 * the freeze rule (a version's meaning may never change once a row uses it).
 *
 * ── Security note (Friction Rule clause 4 — removing friction is a security
 * change) ───────────────────────────────────────────────────────────────────
 *
 * This canonicalization is a COARSENING: it maps more raw strings onto one
 * digest, so a guesser needs the answer only up to case, spacing, punctuation,
 * and Latin accents. For the short phrases these questions take that is a small,
 * real reduction in an attacker's work — accepted deliberately, because the
 * control this gate provides is "does this person know the private fact", and a
 * control that fails people who DO know the fact protects nobody. The
 * load-bearing bounds are unchanged: 3 attempts per invite then auto-revoke, the
 * invite's own expiry, the per-IP rate limit, and the fact that raw answers
 * never reach the server (Rule 5) — only salted SHAKE256 digests (Rule 1 /
 * L0-16, formula untouched).
 */

/**
 * Apostrophes — including the curly U+2019 a mobile keyboard substitutes and the
 * two spacing-accent characters some layouts produce. Removed OUTRIGHT (not
 * spaced) so a contraction collapses the way a human would retype it without
 * one: "o'brien" and "obrien" both become "obrien".
 *
 * Applied BEFORE NFKD, which is load-bearing for U+00B4 ACUTE ACCENT: NFKD
 * decomposes it to SPACE + U+0301, and the combining mark is then stripped
 * while the SPACE survives — so "d´artagnan" would become "d artagnan" while
 * "d'artagnan" became "dartagnan", splitting exactly the mobile-substitution
 * class this exists to absorb. Stripping first removes the character before it
 * can decompose.
 */
import {
  CURRENT_GUARDIAN_ANSWER_CANON_VERSION,
  type GuardianAnswerCanonVersion,
} from "./canon-version";

const APOSTROPHES = /['‘’ʼ`´]/gu;

/**
 * Every other punctuation mark or symbol becomes a SPACE, not nothing, so a
 * hyphenated phrase and its spaced twin agree: "well-known" and "well known"
 * both become "well known". Deleting it instead would have made them differ.
 */
const PUNCTUATION_OR_SYMBOL = /[\p{P}\p{S}]/gu;

/**
 * Invisible format characters that survive a paste and are impossible for the
 * typist to see or reproduce: ZWSP/ZWNJ/ZWJ, soft hyphen, directional marks.
 * Stripped outright — they are not word separators.
 */
const INVISIBLE_FORMAT = /\p{Cf}/gu;

/** Any unicode separator (incl. NBSP) or control whitespace → a single space. */
const ANY_WHITESPACE = /[\p{Z}\s]+/gu;

/**
 * LATIN combining diacritics ONLY (U+0300–U+036F), left behind by NFKD
 * decomposition: é → e + U+0301.
 *
 * Deliberately NOT `\p{M}` (all combining marks). `\p{M}` would also strip
 * Japanese dakuten (がっこう → かっこう), Devanagari vowel signs (हिंदी → हद), and
 * Thai/Arabic marks — collapsing SEMANTICALLY DISTINCT words onto one digest in
 * those scripts. That is a far larger coarsening than dropping a French accent,
 * and it is not one this gate should make silently. Narrowing to the Latin
 * block keeps the "café = cafe" win without touching non-Latin answers.
 */
const LATIN_DIACRITICS = /[\u0300-\u036F]/gu;

/**
 * The minimal fold — canonical form **v1**: case + edge whitespace only.
 *
 * This was the WHOLE canonicalization until 2026-08-04, so every digest stored
 * before migration 0129 was computed with it and can only ever be reproduced by
 * it. It is therefore NOT dead code and NOT merely the empty-result fallback:
 * it is a live, permanently-frozen version, selected by
 * `answer_canon_version = 1` on the stored row. Changing what it computes
 * invalidates every one of those digests. Do not "simplify" it.
 *
 * It doubles as the empty-result fallback for v2 (see below), which is why the
 * two uses share one function rather than two copies that could drift.
 */
function minimalFold(raw: string): string {
  return raw.toLowerCase().normalize("NFKC").trim();
}

/**
 * The canonical form every guardian answer is hashed under, on both sides.
 *
 * Order is load-bearing:
 *  - apostrophes BEFORE NFKD (see APOSTROPHES — U+00B4 would otherwise leave a
 *    space behind);
 *  - decompose (NFKD) before stripping diacritics, or precomposed accented
 *    characters survive as single code points;
 *  - strip before recomposing (NFKC), so the recomposition has nothing to put
 *    back.
 *
 * EMPTY-RESULT GUARD: an answer made only of punctuation, symbols, or emoji
 * ("!!!", "🎂") would otherwise canonicalize to "" — and "" is a universal key
 * that any blank or punctuation-only submission reproduces, turning a stored
 * answer into one that everybody can pass. When the coarsening would empty the
 * answer, fall back to `minimalFold`, which keeps such answers distinct from
 * each other AND from a blank submission.
 *
 * HONEST LIMIT on that branch: an answer routed through the fallback gets NONE
 * of the widening — stored "!!!" is not matched by "! ! !". This is a deliberate
 * safety trade (a universal key is far worse than a strict rare case), and
 * QuestionCreator additionally refuses to STORE a content-free answer, so the
 * branch is unreachable from this product's own writer.
 *
 * ALSO NOT ABSORBED (named because the ward-facing copy says "capitals … are
 * ignored"): `toLowerCase` is not a full Unicode case fold, so German ß
 * ("STRASSE" vs "Straße"), Greek final sigma, and Turkish dotless ı can still
 * disagree between an uppercase and a lowercase typist. Latin-script answers
 * without those characters — the overwhelming majority — are unaffected.
 */
export function normalizeVerificationAnswer(
  raw: string,
  version: GuardianAnswerCanonVersion = CURRENT_GUARDIAN_ANSWER_CANON_VERSION,
): string {
  // v1 is a FROZEN historical form, not a degraded mode — a row stamped v1 was
  // genuinely hashed this way and can be matched no other way. Dispatching here
  // rather than at every call site means a caller that forgets the argument
  // gets the current form (correct for a fresh invite) instead of silently
  // choosing the wrong one.
  if (version === 1) return minimalFold(raw);

  const coarse = raw
    .replace(APOSTROPHES, "")
    .normalize("NFKD")
    .replace(LATIN_DIACRITICS, "")
    .replace(INVISIBLE_FORMAT, "")
    .toLowerCase()
    .replace(PUNCTUATION_OR_SYMBOL, " ")
    .replace(ANY_WHITESPACE, " ")
    .normalize("NFKC")
    // trim LAST: NFKC can itself emit an edge space (a spacing-accent character
    // decomposes to SPACE + combining mark), so trimming before it would leave
    // that space in the canonical form.
    .trim();

  return coarse === "" ? minimalFold(raw) : coarse;
}
